<script setup>
import { Icon } from '@iconify/vue';

defineProps({
    title: {
        type: String,
        default: '',
    },
    subtitle: {
        type: String,
        default: '',
    },
    icon: {
        type: String,
        default: '',
    },
    iconColor: {
        type: String,
        default: 'indigo', // indigo, blue, emerald, amber, rose, purple
    },
    badge: {
        type: [String, Number],
        default: null,
    },
    badgeColor: {
        type: String,
        default: 'indigo',
    },
    loading: {
        type: Boolean,
        default: false,
    },
    error: {
        type: String,
        default: null,
    },
    showReload: {
        type: Boolean,
        default: true,
    },
    empty: {
        type: Boolean,
        default: false,
    },
    emptyMessage: {
        type: String,
        default: 'No se encontraron registros disponibles.',
    },
});

const emit = defineEmits(['reload']);

const colorClasses = {
    indigo: {
        iconBg: 'bg-indigo-50 text-indigo-600 dark:bg-indigo-950/40 dark:text-indigo-400',
        badge: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-300 border-indigo-200/60 dark:border-indigo-800',
        borderBottom: 'border-b-indigo-500',
    },
    blue: {
        iconBg: 'bg-blue-50 text-blue-600 dark:bg-blue-950/40 dark:text-blue-400',
        badge: 'bg-blue-50 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300 border-blue-200/60 dark:border-blue-800',
        borderBottom: 'border-b-blue-500',
    },
    emerald: {
        iconBg: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400',
        badge: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 border-emerald-200/60 dark:border-emerald-800',
        borderBottom: 'border-b-emerald-500',
    },
    amber: {
        iconBg: 'bg-amber-50 text-amber-600 dark:bg-amber-950/40 dark:text-amber-400',
        badge: 'bg-amber-50 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300 border-amber-200/60 dark:border-amber-800',
        borderBottom: 'border-b-amber-500',
    },
    rose: {
        iconBg: 'bg-rose-50 text-rose-600 dark:bg-rose-950/40 dark:text-rose-400',
        badge: 'bg-rose-50 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300 border-rose-200/60 dark:border-rose-800',
        borderBottom: 'border-b-rose-500',
    },
    purple: {
        iconBg: 'bg-purple-50 text-purple-600 dark:bg-purple-950/40 dark:text-purple-400',
        badge: 'bg-purple-50 text-purple-700 dark:bg-purple-950/60 dark:text-purple-300 border-purple-200/60 dark:border-purple-800',
        borderBottom: 'border-b-purple-500',
    },
};
</script>

<template>
    <div class="bg-white dark:bg-slate-900 rounded-2xl border border-gray-200/80 dark:border-slate-800 shadow-sm transition hover:shadow-md flex flex-col overflow-hidden">
        <!-- Cabecera del Widget -->
        <div class="p-4 sm:p-5 border-b border-gray-100 dark:border-slate-800/80 flex items-center justify-between gap-3 shrink-0">
            <div class="flex items-center gap-3 min-w-0">
                <!-- Icono temático -->
                <div
                    v-if="icon"
                    class="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 shadow-xs"
                    :class="colorClasses[iconColor]?.iconBg || colorClasses.indigo.iconBg"
                >
                    <Icon :icon="icon" class="w-5 h-5" />
                </div>

                <div class="truncate">
                    <div class="flex items-center gap-2">
                        <h3 class="text-base font-bold text-gray-900 dark:text-white truncate leading-snug">
                            {{ title }}
                        </h3>
                        <span
                            v-if="badge !== null && badge !== undefined"
                            class="px-2 py-0.5 text-xs font-bold rounded-full border shrink-0"
                            :class="colorClasses[badgeColor]?.badge || colorClasses.indigo.badge"
                        >
                            {{ badge }}
                        </span>
                    </div>
                    <p v-if="subtitle" class="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                        {{ subtitle }}
                    </p>
                </div>
            </div>

            <!-- Acciones de Cabecera -->
            <div class="flex items-center gap-1.5 shrink-0">
                <slot name="header-actions" />

                <!-- Botón de recarga si está habilitado -->
                <button
                    v-if="showReload"
                    type="button"
                    @click="emit('reload')"
                    :disabled="loading"
                    title="Actualizar datos"
                    class="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition disabled:opacity-50"
                >
                    <Icon icon="tabler:refresh" class="w-4 h-4" :class="{ 'animate-spin text-indigo-600': loading }" />
                </button>
            </div>
        </div>

        <!-- Cuerpo del Widget -->
        <div class="p-4 sm:p-5 flex flex-col">
            <!-- 1. Estado de Carga (Skeleton Shimmer) -->
            <div v-if="loading" class="space-y-3">
                <slot name="loading">
                    <div class="animate-pulse space-y-3">
                        <div class="h-12 bg-gray-100 dark:bg-slate-800/80 rounded-xl w-full"></div>
                        <div class="h-12 bg-gray-100 dark:bg-slate-800/80 rounded-xl w-full"></div>
                        <div class="h-12 bg-gray-100 dark:bg-slate-800/80 rounded-xl w-3/4"></div>
                    </div>
                </slot>
            </div>

            <!-- 2. Estado de Error o Lentitud -->
            <div
                v-else-if="error"
                class="p-4 rounded-xl bg-rose-50/80 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900/50 flex flex-col items-center justify-center text-center gap-2"
            >
                <div class="w-8 h-8 rounded-full bg-rose-100 dark:bg-rose-900/40 text-rose-600 flex items-center justify-center">
                    <Icon icon="tabler:alert-triangle" class="w-5 h-5" />
                </div>
                <div>
                    <p class="text-xs font-semibold text-rose-800 dark:text-rose-300">
                        {{ error }}
                    </p>
                    <p class="text-[11px] text-rose-600/80 dark:text-rose-400 mt-0.5">
                        El servidor tardó en responder o no se pudo cargar la información.
                    </p>
                </div>
                <button
                    type="button"
                    @click="emit('reload')"
                    class="mt-1 px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-xs font-semibold shadow-xs transition flex items-center gap-1.5"
                >
                    <Icon icon="tabler:reload" class="w-3.5 h-3.5" />
                    <span>Reintentar</span>
                </button>
            </div>

            <!-- 3. Estado Vacío -->
            <div v-else-if="empty" class="py-4 text-center">
                <slot name="empty">
                    <div class="p-6 border border-dashed border-gray-200 dark:border-slate-800 rounded-2xl flex flex-col items-center justify-center text-center gap-2">
                        <Icon icon="tabler:inbox" class="w-8 h-8 text-gray-300 dark:text-slate-600" />
                        <p class="text-xs text-gray-500 dark:text-gray-400 max-w-xs">
                            {{ emptyMessage }}
                        </p>
                    </div>
                </slot>
            </div>

            <!-- 4. Contenido Real -->
            <div v-else class="w-full">
                <slot />
            </div>
        </div>

        <!-- Pie opcional si se proporciona slot -->
        <div v-if="$slots.footer" class="p-3 sm:p-4 bg-gray-50/70 dark:bg-slate-800/40 border-t border-gray-100 dark:border-slate-800 shrink-0">
            <slot name="footer" />
        </div>
    </div>
</template>
